import React, { useEffect, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navigation from './components/Navigation';
import Home from './pages/Home';
import Services from './pages/Services';
import About from './pages/About';
import Contact from './pages/Contact';
import CGV from './pages/CGV';
import './App.css';

gsap.registerPlugin(ScrollTrigger);

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const scrollProgressRef = useRef(null);
  const cursorRef = useRef(null);

  useEffect(() => {
    // Barre de progression de scroll
    const updateScrollProgress = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );
      
      const maxScroll = documentHeight - windowHeight;
      const scrollPercent = maxScroll > 0 ? (scrollTop / maxScroll) * 100 : 0;
      
      if (scrollProgressRef.current) {
        const scaleValue = Math.min(Math.max(scrollPercent / 100, 0), 1);
        gsap.to(scrollProgressRef.current, {
          scaleY: scaleValue,
          duration: 0.1,
          ease: "none"
        });
      }
    };

    // Curseur personnalisé
    const updateCursor = (e) => {
      if (cursorRef.current && window.innerWidth > 768) {
        gsap.to(cursorRef.current, {
          x: e.clientX,
          y: e.clientY,
          duration: 0.1,
          ease: "power2.out"
        });
      }
    };

    const handleMouseEnter = () => {
      if (cursorRef.current && window.innerWidth > 768) {
        cursorRef.current.classList.add('hover');
      }
    };

    const handleMouseLeave = () => {
      if (cursorRef.current && window.innerWidth > 768) {
        cursorRef.current.classList.remove('hover');
      }
    };

    const handleMouseDown = () => {
      if (cursorRef.current && window.innerWidth > 768) {
        cursorRef.current.classList.add('click');
      }
    };

    const handleMouseUp = () => {
      if (cursorRef.current && window.innerWidth > 768) {
        cursorRef.current.classList.remove('click');
      }
    };

    window.addEventListener('scroll', updateScrollProgress);
    window.addEventListener('resize', updateScrollProgress);
    window.addEventListener('mousemove', updateCursor);
    
    // Éléments interactifs
    const interactiveElements = document.querySelectorAll('a, button, input, textarea, select, [role="button"]');
    interactiveElements.forEach(el => {
      el.addEventListener('mouseenter', handleMouseEnter);
      el.addEventListener('mouseleave', handleMouseLeave);
    });

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    
    // Initialiser la barre
    setTimeout(updateScrollProgress, 100);

    return () => {
      window.removeEventListener('scroll', updateScrollProgress);
      window.removeEventListener('resize', updateScrollProgress);
      window.removeEventListener('mousemove', updateCursor);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      
      interactiveElements.forEach(el => {
        el.removeEventListener('mouseenter', handleMouseEnter);
        el.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, []);

  return (
    <div className="App">
      <ScrollToTop />
      <Navigation />
      
      {/* Curseur personnalisé */}
      <div ref={cursorRef} className="cursor"></div>
      
      {/* Barre de progression de scroll */}
      <div className="scroll-progress">
        <div ref={scrollProgressRef} className="scroll-progress-bar"></div>
      </div>

      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/a-propos" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/cgv" element={<CGV />} />
        </Routes>
      </main>
    </div>
  );
}

export default App; 